import { canv } from "@/canv/global";
import { arrowWidget } from "@/elements/arrow";
import type { stickerWidget } from "@/elements/sticker";
import type { Group } from "@/group";
import type { Action, CatchPointer, DistInfoX, DistInfoY, Line } from "./types";

class TransformManager {
    resizing(xMove: number, yMove: number) {
        // Идея:
        // Формируем targetProps - в которой будут наши группы
        // Формируя targetProps учитываем за какой catchPointer тянем
        // Можно представить как рамку, в которую будем располагать наши группы
        // Для каждой группы запоминаем widthRatio, heightRatio, xRatio, yRatio
        // После этого рейсайзим объекты, чтобы они занимали столько же места в новом targetProps

        if (this.wideSticker(xMove, yMove)) {
            return;
        }
        if (["sideLeft", "sideRight"].includes(this.catchPointer)) {
            yMove = 0;
        }
        if (["sideTop", "sideBottom"].includes(this.catchPointer)) {
            xMove = 0;
        }
        if (["bottomLeft", "upLeft", "sideLeft"].includes(this.catchPointer)) {
            xMove *= -1;
        }
        if (["upLeft", "upRight", "sideTop"].includes(this.catchPointer)) {
            yMove *= -1;
        }

        const savedProps = canv.canvasController.getSelectedProps();

        const targetProps = { ...savedProps };

        if (
            ["bottomLeft", "bottomRight", "upLeft", "upRight"].includes(this.catchPointer) &&
            canv.canvasController.selected.length > 1
        ) {
            const whRatio = savedProps.width / savedProps.height;
            if (Math.abs(xMove) > Math.abs(yMove)) {
                targetProps.width += xMove;
                targetProps.height = targetProps.width / whRatio;
            } else {
                targetProps.height += yMove;
                targetProps.width = targetProps.height * whRatio;
            }
        } else {
            targetProps.width += xMove;
            targetProps.height += yMove;
        }

        // Обрабатываем отрицательные размеры
        if (targetProps.width < 0) {
            targetProps.width *= -1;
            targetProps.startX -= targetProps.width;
            this.setCatchPointer(this.getOpositeCatchPointer("horizontal"));
        }
        if (targetProps.height < 0) {
            targetProps.height *= -1;
            targetProps.startY -= targetProps.height;
            this.setCatchPointer(this.getOpositeCatchPointer("vertical"));
        }

        // для групп фиксим точки противоположные catchPointer и фиксим для влева и вверх знаки
        if (canv.canvasController.selected.length > 1) {
            const dx = targetProps.width - savedProps.width;
            const dy = targetProps.height - savedProps.height;

            switch (this.catchPointer) {
                case "bottomLeft":
                    targetProps.startX = savedProps.startX - dx;
                    targetProps.startY = savedProps.startY;
                    break;
                case "upLeft":
                    targetProps.startX = savedProps.startX - dx;
                    targetProps.startY = savedProps.startY - dy;
                    break;
                case "upRight":
                    targetProps.startX = savedProps.startX;
                    targetProps.startY = savedProps.startY - dy;
                    break;
                case "sideLeft": // чтобы не ломалась логика расширения групп эти две sideLeft sideTop стороны надо изменить
                    targetProps.startX -= xMove;
                    break;
                case "sideTop":
                    targetProps.startY -= yMove;
                    break;
                // bottomRight  работает правильно
            }
        }

        // // Корректировка позиции только для элементов
        if (canv.canvasController.selected.length === 1) {
            if (this.catchPointer === "bottomLeft" || this.catchPointer === "sideLeft") {
                targetProps.startX -= xMove;
            } else if (this.catchPointer === "upRight" || this.catchPointer === "sideTop") {
                targetProps.startY -= yMove;
            } else if (this.catchPointer === "upLeft") {
                targetProps.startX -= xMove;
                targetProps.startY -= yMove;
            }
        }

        const gData = canv.canvasController.selected.map((itemElement) => ({
            widthRatio: savedProps.width !== 0 ? itemElement.width / savedProps.width : 0.01,
            heightRatio: savedProps.height !== 0 ? itemElement.height / savedProps.height : 0.01,
            xRatio:
                savedProps.width !== 0
                    ? (itemElement.startX - savedProps.startX) / savedProps.width
                    : 0.01,
            yRatio:
                savedProps.height !== 0
                    ? (itemElement.startY - savedProps.startY) / savedProps.height
                    : 0.01,
        }));

        canv.canvasController.selected.forEach((itemElement, i) => {
            itemElement.resizeGroup(
                targetProps.width * gData[i].widthRatio - itemElement.width,
                targetProps.height * gData[i].heightRatio - itemElement.height,
                this.catchPointer,
            );
            itemElement.translateGroup(
                targetProps.startX - itemElement.startX + gData[i].xRatio * targetProps.width,
                targetProps.startY - itemElement.startY + gData[i].yRatio * targetProps.height,
                true,
            );
        });

        // все кроме shape меняет размер не точно на то, что передаешь в resize, поэтому
        // Если в selected только shape, от код далее был бы не нужен, а так он гарантирует, что противоположный угол останется на месте

        if (canv.canvasController.selected.length === 1) {
            const currentProps = canv.canvasController.getSelectedProps();
            const itemElement = canv.canvasController.selected[0];
            if (this.catchPointer === "bottomLeft" || this.catchPointer === "sideLeft") {
                itemElement.translateGroup(
                    targetProps.startX +
                        targetProps.width -
                        (currentProps.startX + currentProps.width),
                    targetProps.startY - currentProps.startY,
                );
            } else if (this.catchPointer === "upRight" || this.catchPointer === "sideTop") {
                itemElement.translateGroup(
                    targetProps.startX - currentProps.startX,
                    targetProps.startY +
                        targetProps.height -
                        (currentProps.startY + currentProps.height),
                );
            } else if (this.catchPointer === "upLeft") {
                itemElement.translateGroup(
                    targetProps.startX +
                        targetProps.width -
                        (currentProps.startX + currentProps.width),
                    targetProps.startY +
                        targetProps.height -
                        (currentProps.startY + currentProps.height),
                );
            } else {
                itemElement.translateGroup(
                    targetProps.startX - currentProps.startX,
                    targetProps.startY - currentProps.startY,
                );
            }
        }

        //такая же тема как и выше постфикс после всех изменений, чтобы не дергался при bottomLeft и upLeft угол напротив
        if (canv.canvasController.selected.length > 1) {
            const currentProps = canv.canvasController.getSelectedProps();
            const dx =
                savedProps.startX + savedProps.width - (currentProps.startX + currentProps.width);
            const dy =
                savedProps.startY + savedProps.height - (currentProps.startY + currentProps.height);

            switch (this.catchPointer) {
                case "bottomLeft":
                    canv.canvasController.selected.forEach((itemElement) => {
                        itemElement.translateGroup(dx, 0);
                    });
                    break;
                case "upLeft":
                    canv.canvasController.selected.forEach((itemElement) => {
                        itemElement.translateGroup(dx, dy);
                    });
                    break;
                case "upRight":
                    canv.canvasController.selected.forEach((itemElement) => {
                        itemElement.translateGroup(0, dy);
                    });
                    break;
                // bottomRight  работает правильно
            }
        }
    }

    wideSticker(xMove: number, yMove: number) {
        this.resizeEntireMoveX += xMove;
        this.resizeEntireMoveY += yMove;

        const onlySticker =
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper() &&
            canv.canvasController.selected[0].onlyChild()?.constructorName === "sticker";

        let correctCatchPointer = false;
        let resizeEntireMoveCoordinate = this.resizeEntireMoveX;

        if (onlySticker) {
            const sticker =
                canv.canvasController.selected[0].onlyChild() as unknown as stickerWidget;
            const angle = Math.abs(sticker.parent.angle) % 360;
            const isSwapped = (angle >= 70 && angle < 160) || (angle >= 250 && angle < 340);
            if (
                isSwapped &&
                (this.catchPointer === "sideTop" || this.catchPointer === "sideBottom")
            ) {
                correctCatchPointer = true;
                resizeEntireMoveCoordinate = this.resizeEntireMoveY;
            } else if (
                !isSwapped &&
                (this.catchPointer === "sideLeft" || this.catchPointer === "sideRight")
            ) {
                correctCatchPointer = true;
            }
        }

        if (onlySticker && correctCatchPointer) {
            const sticker =
                canv.canvasController.selected[0].onlyChild() as unknown as stickerWidget;
            const moveThreshold = 40;

            if (resizeEntireMoveCoordinate > canv.actionManager.getZoomConstant(moveThreshold)) {
                sticker.makeWide();
                this.resizeEntireMoveX = 0;
                this.resizeEntireMoveY = 0;
            } else if (
                resizeEntireMoveCoordinate < canv.actionManager.getZoomConstant(-moveThreshold)
            ) {
                sticker.makeNormal();
                this.resizeEntireMoveX = 0;
                this.resizeEntireMoveY = 0;
            }

            return true;
        }

        return false;
    }

    actionAndMag(action: Action) {
        const pt = canv.actionManager.getActionPoint();

        pt.x = pt.x * canv.m[0];
        pt.y = pt.y * canv.m[3];

        const prePt = canv.actionManager.getPreActionPoint();

        const preX = prePt.x * canv.m[0];
        const preY = prePt.y * canv.m[3];

        let translateX = (pt.x - preX) / canv.m[0];
        let translateY = (pt.y - preY) / canv.m[3];

        const gap = 5 / canv.m[0];
        const realGap = gap * canv.m[0];

        let allowYMag = true;
        let allowXMag = true;

        if (this.magExistsY) {
            const realMagMoveY = this.magMoveY * canv.m[0];

            if (translateY < 0) {
                if (pt.y < this.magPtY + realMagMoveY - realGap) {
                    const calcedBonus = (this.magPtY + realMagMoveY - realGap - pt.y) / canv.m[0];

                    if (translateY !== 0)
                        translateY = (translateY / Math.abs(translateY)) * (gap + calcedBonus);

                    this.magExistsY = false;
                    this.hLevelLines = [];
                    this.vDist = [];
                } else {
                    translateY = 0;
                    allowYMag = false;
                }
            } else {
                if (pt.y > this.magPtY + (realGap + realMagMoveY)) {
                    const calcedBonus =
                        (pt.y - (this.magPtY + (realGap + realMagMoveY))) / canv.m[0];

                    if (translateY !== 0)
                        translateY = (translateY / Math.abs(translateY)) * (gap + calcedBonus);

                    this.magExistsY = false;
                    this.hLevelLines = [];
                    this.vDist = [];
                } else {
                    translateY = 0;
                    allowYMag = false;
                }
            }
        }

        if (this.magExistsX) {
            const realMagMoveX = this.magMoveX * canv.m[0];

            if (translateX < 0) {
                if (pt.x < this.magPtX + realMagMoveX - realGap) {
                    const calcedBonus = (this.magPtX + realMagMoveX - realGap - pt.x) / canv.m[0];

                    if (translateX !== 0)
                        translateX = (translateX / Math.abs(translateX)) * (gap + calcedBonus);

                    this.magExistsX = 0;
                    this.vLevelLines = [];
                    this.hDist = [];
                } else {
                    translateX = 0;
                    allowXMag = false;
                }
            } else {
                if (pt.x > this.magPtX + (realGap + realMagMoveX)) {
                    const calcedBonus =
                        (pt.x - (this.magPtX + (realGap + realMagMoveX))) / canv.m[0];

                    if (translateX !== 0)
                        translateX = (translateX / Math.abs(translateX)) * (gap + calcedBonus);

                    this.magExistsX = 0;
                    this.vLevelLines = [];
                    this.hDist = [];
                } else {
                    translateX = 0;
                    allowXMag = false;
                }
            }
        }

        if (action === "translate") {
            for (let i = 0; i < canv.canvasController.selected.length; i++) {
                canv.canvasController.selected[i].translateGroup(translateX, translateY, true);
            }
        } else if (action === "resize") {
            if (
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0].isItemWrapper() &&
                canv.canvasController.selected[0].onlyChild() instanceof arrowWidget
            ) {
                canv.canvasController.selected[0]
                    .onlyChild()
                    ?.resize(translateX, translateY, this.catchPointer);
                canv.redraw();
                return;
            }
            this.resizing(translateX, translateY);
        }

        canv.canvasController.updateDims();

        const selectedRect = {
            startX: canv.canvasController.startX,
            startY: canv.canvasController.startY,
            width: canv.canvasController.width,
            height: canv.canvasController.height,
        };

        // Ищем levelLines

        const result = {
            x: null,
            y: null,
            vLines: [],
            hLines: [],
            hDist: [],
            vDist: [],
        };

        let allowed = ["left", "hCenter", "right", "bottom", "vCenter", "top"];

        if (action === "resize") {
            if (this.catchPointer === "sideLeft") {
                allowed = ["left"];
            } else if (this.catchPointer === "sideRight") {
                allowed = ["right"];
            } else if (this.catchPointer === "sideTop") {
                allowed = ["top"];
            } else if (this.catchPointer === "sideBottom") {
                allowed = ["bottom"];
            } else if (this.catchPointer === "upRight") {
                allowed = ["right", "top"];
            } else if (this.catchPointer === "upLeft") {
                allowed = ["left", "top"];
            } else if (this.catchPointer === "bottomRight") {
                allowed = ["right", "bottom"];
            } else if (this.catchPointer === "bottomLeft") {
                allowed = ["left", "bottom"];
            }
        }

        if (this.visibleItems.length === 0) {
            this.visibleItems = canv.canvasController.getVisibleItemWrappersExceptSelected();

            // this.distancesX = [];
            // this.distancesY = [];

            // for (var i = 0; i < visibleItems.length; i++) {
            //     for (var j = 0; j < visibleItems.length; j++) {
            //         if (visibleItems[i] == visibleItems[j]) continue;
            //         if (visibleItems[i].onlyChild().disableLevelLines || visibleItems[j].onlyChild().disableLevelLines) continue;

            //         if (visibleItems[i].startY <= visibleItems[j].startY + visibleItems[j].height && visibleItems[i].startY + visibleItems[i].height >= visibleItems[j].startY) {
            //             if (visibleItems[i].startX > visibleItems[j].startX + visibleItems[j].width) {
            //                 this.distancesX.push({
            //                     "distance": Math.floor(visibleItems[i].startX - (visibleItems[j].startX + visibleItems[j].width)),
            //                     "startX": visibleItems[j].startX + visibleItems[j].width,
            //                     "endX": visibleItems[i].startX,
            //                     "startUID": visibleItems[i].UID,
            //                     "endUID": visibleItems[j].UID
            //                 });
            //             }
            //         }

            //         if (visibleItems[i].startX <= visibleItems[j].startX + visibleItems[j].width && visibleItems[i].startX + visibleItems[i].width >= visibleItems[j].startX) {
            //             if (visibleItems[i].startY > visibleItems[j].startY + visibleItems[j].height) {
            //                 this.distancesY.push({
            //                     "distance": Math.floor(visibleItems[i].startY - (visibleItems[j].startY + visibleItems[j].height)),
            //                     "startY": visibleItems[j].startY + visibleItems[j].height,
            //                     "endY": visibleItems[i].startY,
            //                     "startUID": visibleItems[i].UID,
            //                     "endUID": visibleItems[j].UID
            //                 });
            //             }
            //         }
            //     }
            // }

            // this.visibleItems = visibleItems;
        }

        // console.log('this.visibleItems', this.visibleItems);
        // console.log('this.distancesX', this.distancesX);
        // console.log('this.distancesY', this.distancesY);

        // var hSortedVisible = [...this.visibleItems];
        // var vSortedVisible = [...this.visibleItems];

        // var hSort = function ( a, b ) {
        //     var aDist = 0;
        //     var bDist = 0;

        //     if (a.startX + a.width < canv.canvasController.startX) {
        //         aDist = canv.canvasController.startX - (a.startX + a.width);
        //     } else if (canv.canvasController.startX + canv.canvasController.width < a.startX) {
        //         aDist = a.startX - (canv.canvasController.startX + canv.canvasController.width);
        //     } else {
        //         aDist = 0;
        //     }

        //     if (b.startX + b.width < canv.canvasController.startX) {
        //         bDist = canv.canvasController.startX - (b.startX + b.width);
        //     } else if (canv.canvasController.startX + canv.canvasController.width < b.startX) {
        //         bDist = b.startX - (canv.canvasController.startX + canv.canvasController.width);
        //     } else {
        //         bDist = 0;
        //     }

        //     if ( aDist < bDist ){
        //         return -1;
        //     }
        //     if ( aDist > bDist ){
        //         return 1;
        //     }
        //     return 0;
        // }

        // var vSort = function ( a, b ) {
        //     var aDist = 0;
        //     var bDist = 0;

        //     if (a.startY + a.height < canv.canvasController.startY) {
        //         aDist = canv.canvasController.startY - (a.startY + a.height);
        //     } else if (canv.canvasController.startY + canv.canvasController.height < a.startY) {
        //         aDist = a.startY - (canv.canvasController.startY + canv.canvasController.height);
        //     } else {
        //         aDist = 0;
        //     }

        //     if (b.startY + b.height < canv.canvasController.startY) {
        //         bDist = canv.canvasController.startY - (b.startY + b.height);
        //     } else if (canv.canvasController.startY + canv.canvasController.height < b.startY) {
        //         bDist = b.startY - (canv.canvasController.startY + canv.canvasController.height);
        //     } else {
        //         bDist = 0;
        //     }

        //     if ( aDist < bDist ){
        //         return -1;
        //     }
        //     if ( aDist > bDist ){
        //         return 1;
        //     }
        //     return 0;
        // }

        // hSortedVisible.sort(hSort);
        // vSortedVisible.sort(vSort);

        // Синие линии (показывают выравнивание по кграням и центру выбранных элементов с остальными, если оно есть)
        for (let i = 0; i < this.visibleItems.length; i++) {
            this.visibleItems[i].TryLevelLines(selectedRect, result, allowed);
        }

        // Красные линии (идут от центра выбранного элемента и доходят до ближайших элементов по x/y)
        // for (var i = 0; i < hSortedVisible.length; i++) {
        //     hSortedVisible[i].TryDistancesX(selectedRect, result, allowed, this.distancesX);
        // }

        // for (var i = 0; i < hSortedVisible.length; i++) {
        //     vSortedVisible[i].TryDistancesY(selectedRect, result, allowed, this.distancesY);
        // }

        if (allowYMag && result.y != null) {
            this.magExistsY = true;
            this.magMoveY = result.y - canv.canvasController.startY;
            this.magPtY = pt.y;

            canv.canvasController.updateDims();

            if (action === "resize") {
                if (
                    this.catchPointer === "sideTop" ||
                    this.catchPointer === "upLeft" ||
                    this.catchPointer === "upRight"
                ) {
                    this.resizing(0, -this.magMoveY);
                }

                if (
                    this.catchPointer === "sideBottom" ||
                    this.catchPointer === "bottomLeft" ||
                    this.catchPointer === "bottomRight"
                ) {
                    this.resizing(0, this.magMoveY);
                }
            } else {
                for (let k = 0; k < canv.canvasController.selected.length; k++) {
                    canv.canvasController.selected[k].translateGroup(0, this.magMoveY, true);
                }
            }

            this.hLevelLines = result.hLines;
            this.vDist = result.vDist;
        }

        if (allowXMag && result.x != null) {
            this.magExistsX = true;
            this.magMoveX = result.x - canv.canvasController.startX;
            this.magPtX = pt.x;

            canv.canvasController.updateDims();

            if (action === "resize") {
                if (
                    this.catchPointer === "sideLeft" ||
                    this.catchPointer === "upLeft" ||
                    this.catchPointer === "bottomLeft"
                ) {
                    this.resizing(this.magMoveX, 0);
                }

                if (
                    this.catchPointer === "sideRight" ||
                    this.catchPointer === "upRight" ||
                    this.catchPointer === "bottomRight"
                ) {
                    this.resizing(this.magMoveX, 0);
                }
            } else {
                for (let k = 0; k < canv.canvasController.selected.length; k++) {
                    canv.canvasController.selected[k].translateGroup(this.magMoveX, 0, true);
                }
            }

            this.vLevelLines = result.vLines;
            this.hDist = result.hDist;
        }

        canv.redraw();
    }

    getOpositeCatchPointer(direction: string): CatchPointer {
        if (direction === "horizontal") {
            if (this.catchPointer === "bottomRight") {
                return "bottomLeft";
            } else if (this.catchPointer === "bottomLeft") {
                return "bottomRight";
            } else if (this.catchPointer === "upRight") {
                return "upLeft";
            } else if (this.catchPointer === "upLeft") {
                return "upRight";
            } else if (this.catchPointer === "sideRight") {
                return "sideLeft";
            } else if (this.catchPointer === "sideLeft") {
                return "sideRight";
            }
        } else {
            if (this.catchPointer === "bottomRight") {
                return "upRight";
            } else if (this.catchPointer === "bottomLeft") {
                return "upLeft";
            } else if (this.catchPointer === "upRight") {
                return "bottomRight";
            } else if (this.catchPointer === "upLeft") {
                return "bottomLeft";
            } else if (this.catchPointer === "sideTop") {
                return "sideBottom";
            } else if (this.catchPointer === "sideBottom") {
                return "sideTop";
            }
        }

        return this.catchPointer;
    }

    drawLevelsAdnDists() {
        const max = 30;

        for (let i = 0; i < this.hLevelLines.length && i <= max; i++) {
            const lineInfo = this.hLevelLines[i];
            const target = canv.canvasController.FindByUID(lineInfo.UID) as Group;

            if (target == null) continue;

            let leftX = target.startX;
            let rightX = canv.canvasController.startX + canv.canvasController.width;

            if (leftX > rightX) {
                rightX = target.startX + target.width;
                leftX = canv.canvasController.startX;
            }

            let levelY = canv.canvasController.startY;

            if (lineInfo.code === "vCenter") {
                levelY = canv.canvasController.startY + canv.canvasController.height / 2;
            } else if (lineInfo.code === "bottom") {
                levelY = canv.canvasController.startY + canv.canvasController.height;
            }

            //canv.ctx.save();
            canv.ctx.beginPath();
            canv.ctx.setLineDash([15, 30]);
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1.5);
            canv.ctx.strokeStyle = "#679bfb";
            canv.ctx.moveTo(leftX, levelY);
            canv.ctx.lineTo(rightX, levelY);
            canv.ctx.stroke();
            canv.ctx.setLineDash([]);
            //canv.ctx.restore();
        }

        for (let i = 0; i < this.vLevelLines.length && i <= max; i++) {
            const lineInfo = this.vLevelLines[i];
            const target = canv.canvasController.FindByUID(lineInfo.UID) as Group;

            if (target == null) continue;

            let oneY = target.startY;
            let twoY = canv.canvasController.startY + canv.canvasController.height;

            if (oneY > twoY) {
                twoY = target.startY + target.height;
                oneY = canv.canvasController.startY;
            }

            let levelX = canv.canvasController.startX;

            if (lineInfo.code === "hCenter") {
                levelX = canv.canvasController.startX + canv.canvasController.width / 2;
            } else if (lineInfo.code === "right") {
                levelX = canv.canvasController.startX + canv.canvasController.width;
            }

            //canv.ctx.save();
            canv.ctx.beginPath();
            canv.ctx.setLineDash([15, 30]);
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1.5);
            canv.ctx.strokeStyle = "#679bfb";
            canv.ctx.moveTo(levelX, oneY);
            canv.ctx.lineTo(levelX, twoY);
            canv.ctx.stroke();
            canv.ctx.setLineDash([]);
            //canv.ctx.restore();
        }

        for (let i = 0; i < this.hDist.length && i <= max; i++) {
            const distInfo = this.hDist[i];

            const firstItem = canv.canvasController.FindByUID(distInfo.startUID) as Group;

            let secondStartY = canv.canvasController.startY;
            let secondHeight = canv.canvasController.height;

            if (distInfo.endUID !== undefined) {
                const secondItem = canv.canvasController.FindByUID(distInfo.endUID) as Group;

                secondStartY = secondItem.startY;
                secondHeight = secondItem.height;
            }

            const minY = Math.max(firstItem.startY, secondStartY);
            const maxY = Math.min(firstItem.startY + firstItem.height, secondStartY + secondHeight);
            //canv.ctx.save();
            canv.ctx.beginPath();
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
            canv.ctx.strokeStyle = "#DE3D3D"; // #6723E0
            canv.ctx.globalAlpha = 0.95;
            canv.ctx.moveTo(distInfo.startX, minY + (maxY - minY) / 2);
            canv.ctx.lineTo(distInfo.endX, minY + (maxY - minY) / 2);
            canv.ctx.moveTo(distInfo.startX, minY + (maxY - minY) / 2 - 10);
            canv.ctx.lineTo(distInfo.startX, minY + (maxY - minY) / 2 + 10);
            canv.ctx.moveTo(distInfo.endX, minY + (maxY - minY) / 2 - 10);
            canv.ctx.lineTo(distInfo.endX, minY + (maxY - minY) / 2 + 10);
            canv.ctx.globalAlpha = 1;
            canv.ctx.stroke();
            //canv.ctx.restore();
        }

        for (let i = 0; i < this.vDist.length && i <= max; i++) {
            const distInfo = this.vDist[i];

            const firstItem = canv.canvasController.FindByUID(distInfo.startUID) as Group;

            let secondStartX = canv.canvasController.startX;
            let secondWidth = canv.canvasController.width;

            if (distInfo.endUID !== undefined) {
                const secondItem = canv.canvasController.FindByUID(distInfo.endUID) as Group;

                secondStartX = secondItem.startX;
                secondWidth = secondItem.width;
            }

            const minX = Math.max(firstItem.startX, secondStartX);
            const maxX = Math.min(firstItem.startX + firstItem.width, secondStartX + secondWidth);
            //canv.ctx.save();
            canv.ctx.beginPath();
            canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
            canv.ctx.strokeStyle = "#DE3D3D"; // #6723E0
            canv.ctx.globalAlpha = 0.95;
            canv.ctx.moveTo(minX + (maxX - minX) / 2, distInfo.startY);
            canv.ctx.lineTo(minX + (maxX - minX) / 2, distInfo.endY);
            canv.ctx.moveTo(minX + (maxX - minX) / 2 - 10, distInfo.startY);
            canv.ctx.lineTo(minX + (maxX - minX) / 2 + 10, distInfo.startY);
            canv.ctx.moveTo(minX + (maxX - minX) / 2 - 10, distInfo.endY);
            canv.ctx.lineTo(minX + (maxX - minX) / 2 + 10, distInfo.endY);
            canv.ctx.globalAlpha = 1;
            canv.ctx.stroke();
            //canv.ctx.restore();
        }
    }

    reset() {
        // Сброс levelLines and Dists
        this.visibleItems = [];

        this.hLevelLines = [];
        this.hDist = [];
        this.magExistsY = false;

        this.vLevelLines = [];
        this.vDist = [];
        this.magExistsX = false;

        // для resize
        this.resizeEntireMoveX = 0;
        this.resizeEntireMoveY = 0;
    }

    setCatchPointer(catchPointer: CatchPointer) {
        this.catchPointer = catchPointer;
    }

    magExistsY: boolean | number = false;
    magExistsX: boolean | number = false;
    magPtX = 0;
    magPtY = 0;
    magMoveY = 0;
    magMoveX = 0;
    hDist: DistInfoX[] = [];
    vDist: DistInfoY[] = [];
    vLevelLines: Line[] = [];
    hLevelLines: Line[] = [];
    visibleItems: Group[] = [];
    // distancesX = [];
    // distancesY = [];

    resizeEntireMoveX = 0;
    resizeEntireMoveY = 0;

    catchPointer: CatchPointer = "upLeft";
}

export const transformManager = new TransformManager();
